import { useRef, useState } from 'react';
import { DangerousHtmlContent, Notification, Overlay, variants } from '@eti/components';
import {
  useProperty,
  useSearch,
  useSnowplowTracking,
  useTranslation,
  useSiteContext,
  useDirection,
} from '@eti/providers';
import { breakpoints, Card, Stack, Text, textVariants, vars } from '@etg/wings';
import { css } from '@eti/styles';
import {
  cancelForAnyReason,
  FLEXIBLE_TICKET as FlexTicket,
  flexibleTicket,
  ProductModal,
} from '@eti/products';
import { useScrollTo, price as priceUtil } from '@eti/utils';
import { connect, type MapDispatchToProps } from 'react-redux';
import { change } from 'redux-form';
import type { Dispatch } from 'redux';
import type { MouseEvent } from 'react';
import { useMediaQuery } from 'usehooks-ts';
import { getMarketingCarrierNamesToDisplayInTaxBreakdown } from '../../../../../utils/carriers';
import { useResultPageContext } from '../contexts/ResultPageContextProvider';
import { preSelectedExtraProductsData } from '../preSelectedProducts/preSelectedExtraProductsUtils';
import { getTripDisplayPrice, usePriceDisplayType } from '../../../utils/resultUtils';
import { getTravelerPrices } from '../../../../../utils/travelers';
import { hasRyanAirTicketingOrMarketingCarrier } from '../../../../../utils/carriersUtils';
import CFCModal from '../../../../../common/cfc-modal/CFCModal';
import CFCInfoCard from '../../../../../common/cfc-info-card/CFCInfoCard';
import type {
  BoundType,
  TripSegmentType,
  PreSelectedExtraProducts,
  BoundsType,
} from '../../../models';
import { useGetAncillaryBundle } from '../../../utils/ancillaryBundleUtils';
import { transformBundleData } from '../../../../../utils/transformBundleData';
import { RESULT } from '../../../../../constants/formNamesConstants';
import { AVAILABLE_BUNDLE } from '../../../constants/resultConstants';
import {
  RECOMMENED_FAREKEY,
  MANUALLY_REMOVED_PRODUCTS,
  MANUALLY_ADDED_PRODUCTS,
} from '../../../../../constants/brandedFareConstants';
import { hasLessThan2hSelfTransfer } from '../utils/trip';
import type { Ticket as TicketInterface, TicketPriceModalInfoProps, TicketType } from './model';
import { Ticket } from './ticket/Ticket';
import { ContinueBooking } from './continueBooking/ContinueBooking';
import { useGetTickets } from './hooks/useGetTickets';
import { TICKET_TYPES } from './constants';
import { ItineraryInfo } from './itinerary';
import { TripDetails } from './tripDetails/TripDetails';
import BaggageInformation from './baggage/BaggageInformation';

const footerClass = css`
  border-top: 1px solid ${vars.colors.inputs.main};
`;

const headerStyles = css`
  position: relative;

  & > button {
    inset-inline-start: 8px;
    position: absolute;
  }
`;

const overlayContentStyles = css({
  '&&': {
    paddingBottom: '250px',
  },
});

const titledCardStyles = css`
  margin-top: 16px;
`;

const JOURNEY_TYPE: Record<string, string> = {
  MULTI_STOP: 'Result.TripDetails.MultiStopTrip.Title',
  ONE_WAY: 'Result.TripDetails.OneWay.Title',
  RETURN: 'Result.TripDetails.ReturnTrip.Title',
};

export interface ItineraryDrawerProps {
  bounds: BoundsType;
  isOpen: boolean;
  isSelectedTrip: boolean;
  isSponsoredTrip: boolean;
  isVI: boolean;
  onClose: (e: MouseEvent<HTMLElement>) => void;
  preSelectedExtraProducts: PreSelectedExtraProducts[];
  setBundleData?: (data: any) => void;
  shouldShowOneWayCombinationInfo?: boolean;
  totalPricePreSelectedProducts: number;
}

type DispatchProps = { setBundleData?: (data: any) => void };

const mapDispatchToProps: MapDispatchToProps<DispatchProps, ItineraryDrawerProps> = (
  dispatch: Dispatch,
) => ({
  setBundleData: (data: any) => dispatch(change(RESULT, AVAILABLE_BUNDLE, data)),
});

type Props = ItineraryDrawerProps & DispatchProps;

const ItineraryDrawer = ({
  bounds,
  isOpen,
  isSelectedTrip,
  isSponsoredTrip,
  isVI,
  onClose,
  preSelectedExtraProducts,
  setBundleData,
  shouldShowOneWayCombinationInfo,
  totalPricePreSelectedProducts,
}: Props) => {
  const { t } = useTranslation();
  const { p } = useProperty();
  const { isRTL } = useDirection();
  const isWiderViewport = useMediaQuery(`(min-width: ${breakpoints._970})`);
  const { language, timeFormat } = useSiteContext();
  const isCFCEnabled = p('IbeClient.CarrierFlightConfirmation.Enabled');
  const { CANCEL_FOR_ANY_REASON_TICKET, FLEXIBLE_TICKET, STANDARD_TICKET } = TICKET_TYPES;
  const [isLoading, setIsLoading] = useState(false);
  const { isSnowplowTrackingEnabled, sendTrackEvent } = useSnowplowTracking();
  const [isCFCModalOpen, setIsCFCModalOpen] = useState(false);
  const [isFlexibleTicketPersuasionModalTouched, setIsFlexibleTicketPersuasionModalTouched] =
    useState(false);
  const isDBFEnabled = p('TravelerDetails.DynamicFlow.Enabled');
  const {
    addTrip,
    addTripForSelectedTrip,
    onShowAncillaryBundleModal,
    redirectToDBF,
    redirectToTravelerDetails,
    selectedFlightDetails,
  } = useResultPageContext();
  const addTripWithData = isSelectedTrip ? addTripForSelectedTrip : addTrip;
  const hasLessThan2hSelfTransferTrip = hasLessThan2hSelfTransfer(language, timeFormat, bounds);

  const {
    shareableUrl,
    selectionKey,
    availableExtraProducts,
    includedCabinBaggage,
    includedCheckedBaggage,
    type,
    voucherAmount,
    paymentMethodPrices,
    travelerPrices: resultTravelerPrices,
    tripTravelers,
    travelerPricesWithoutPaymentDiscounts: travelerPricesWithoutDiscount,
  } = selectedFlightDetails.flight ?? {};

  const baggageInfo = {
    availableExtraProducts,
    includedCabinBaggage,
    includedCheckedBaggage,
  };

  const wrapperRef = useRef<HTMLDivElement>(null);
  const scollToRef = useRef<HTMLDivElement>(null);
  const { scrollVertical } = useScrollTo<HTMLDivElement>(scollToRef, { scrollTimeout: 250 });
  const [selectedTicketType, setSelectedTicketType] = useState<TicketType>();
  const [hasError, setHasError] = useState(false);
  const { priceDisplayType, priceDisplayText } = usePriceDisplayType();

  const zeroTaxesCarriers = p('BookingFlow.HideTaxIfZero.Carriers');
  const carriersToDisplayInTaxBreakdown = p('BookingFlow.SupplierCostMarkupBreakdown.Carriers');
  const marketingCarrierNames = getMarketingCarrierNamesToDisplayInTaxBreakdown(
    bounds,
    carriersToDisplayInTaxBreakdown,
    zeroTaxesCarriers,
  );

  const travelerPrices = getTravelerPrices(tripTravelers, resultTravelerPrices);
  const travelerPriceBreakdown = getTravelerPrices(tripTravelers, travelerPricesWithoutDiscount);
  const discountAmount = voucherAmount?.value ?? 0;

  const ticketPriceModalInfo: TicketPriceModalInfoProps = {
    discountAmount,
    marketingCarrierNames,
    paymentMethodPrices,
    preSelectedExtraProducts,
    travelerPriceBreakdown,
    travelerPrices,
  };

  const tickets = useGetTickets(
    availableExtraProducts,
    bounds,
    preSelectedExtraProducts,
    travelerPrices,
    discountAmount,
  );

  const ticketVersion = p('Result.Page.Ticket.Version');
  const isMultipleTickets = tickets.length > 1;
  let ticket = null;
  if (!isMultipleTickets) {
    ticket = { ...tickets[0] };
  } else if (selectedTicketType && ticketVersion !== 1) {
    const filteredTicket = tickets.filter(
      ({ ticketType }: TicketInterface) => ticketType === selectedTicketType,
    );
    ticket = { ...filteredTicket[0] };
  }

  const onSelectHandler = (ticketType: TicketType) => {
    setHasError(false);
    setSelectedTicketType(ticketType);
    scrollVertical('smooth');
  };

  const isFlexibleTicketExists = isMultipleTickets && tickets[1].ticketType === FLEXIBLE_TICKET;
  const isFlexibleTicketPopupEnabled = p('Result.Page.V3.ItineraryDrawer.FlexPopup.Enabled');
  let showFlexibleTicketPersuasionModal = isFlexibleTicketPopupEnabled && isFlexibleTicketExists;

  const getAncillaryBundle = useGetAncillaryBundle(selectionKey as string);
  const getAncillaryBundles = async (ticketType = selectedTicketType) => {
    let ancillaryBundle = [];

    if (p('Result.Show.Overlay')) {
      const { data } = await getAncillaryBundle();
      ancillaryBundle = data?.ancillaryBundle ?? [];
    }

    if (ancillaryBundle.length > 0) {
      const ancillaryBundleData = transformBundleData(ancillaryBundle[0]);

      setBundleData?.({ ...ancillaryBundleData, selectionKey });
      onShowAncillaryBundleModal();
    } else if (ticketType !== FLEXIBLE_TICKET && isDBFEnabled) {
      redirectToDBF();
    } else {
      redirectToTravelerDetails();
    }
  };

  const onClickHandler = async (ticketType = selectedTicketType) => {
    showFlexibleTicketPersuasionModal =
      showFlexibleTicketPersuasionModal && ticketType === STANDARD_TICKET;
    if (showFlexibleTicketPersuasionModal && !isFlexibleTicketPersuasionModalTouched) {
      setIsFlexibleTicketPersuasionModalTouched(true);
      return;
    }
    if (isFlexibleTicketPersuasionModalTouched) {
      setIsFlexibleTicketPersuasionModalTouched(false);
    }
    setSelectedTicketType(ticketType);
    if (isMultipleTickets && ticketVersion !== 1 && ticketType === undefined) {
      scrollVertical('smooth');
      setHasError(true);
      return;
    }
    if (!isLoading) {
      setIsLoading(true);
      try {
        const { hasCFARTicketPreSelected, hasFlexTicketPreSelected, hasPreSelectedProducts } =
          preSelectedExtraProductsData(preSelectedExtraProducts);
        await addTripWithData(
          {
            variables: {
              selectionKey,
              ...((ticketType === FLEXIBLE_TICKET || hasFlexTicketPreSelected) && {
                extraProductSelection: [String(flexibleTicket.id)],
              }),
              ...((ticketType === CANCEL_FOR_ANY_REASON_TICKET || hasCFARTicketPreSelected) && {
                extraProductSelection: [String(cancelForAnyReason.id)],
              }),
            },
          },
          () => getAncillaryBundles(ticketType),
          hasPreSelectedProducts,
        );
      } finally {
        localStorage.removeItem(RECOMMENED_FAREKEY);
        localStorage.removeItem(MANUALLY_REMOVED_PRODUCTS);
        localStorage.removeItem(MANUALLY_ADDED_PRODUCTS);
        if (wrapperRef.current) {
          setIsLoading(false);
        }
      }
    }
  };

  const cityNameAndCodeExtractor = (
    mode: 'origin' | 'destination',
    bound: BoundType,
  ): [string, string] => {
    const segments = [...bound.segments];
    const segment = (mode === 'origin' ? segments.shift() : segments.pop()) as TripSegmentType;
    const { cityCode, cityName } = segment[mode];

    return [cityCode, cityName];
  };

  const shouldShowItineraryInfo = p('Result.Page.Drawer.Itinerary.Info.Enabled');
  const shouldShowTripDetails = p('Result.Page.Drawer.Trip.Details.Enabled');
  const shouldShowBaggageInfo = p('Result.Page.Drawer.Baggage.Enabled');
  const shouldShowSaveTrip = p('Result.SaveTrip.Enabled');
  const shouldShowShareTrip = p('Result.Page.Drawer.ShareTrip.Enabled') && shareableUrl;
  const shouldShowTickets = p('Result.Page.Drawer.Tickets.Enabled');

  const totalPrice = priceUtil.formatPrice(
    getTripDisplayPrice(priceDisplayType)(travelerPrices) + totalPricePreSelectedProducts,
  );
  const isSeparate = p('Result.Page.V3.BaggageAccordion.IsSeparate');
  const { currentSearchData } = useSearch();
  const hideFooterForTicketVersionsList = [1, 4, 5];

  const isRyanAirTicketingOrMarketingCarrierPresent = hasRyanAirTicketingOrMarketingCarrier(
    p('IbeClient.RyanairMarketingCarriers'),
    bounds,
  );

  const handleCFCTitledCardClick = () => {
    setIsCFCModalOpen(true);
    if (isSnowplowTrackingEnabled) {
      sendTrackEvent({
        category: 'Results',
        action: 'Click',
        label: 'Booking process information',
        property: 'Ryanair',
      });
    }
  };
  const firstBound = bounds[0];
  const [originCityCode, originCityName] = cityNameAndCodeExtractor('origin', firstBound);
  const [destinationCityCode, destinationCityName] = cityNameAndCodeExtractor(
    'destination',
    firstBound,
  );

  const wideScreenTitle = isRTL
    ? `${destinationCityName} - ${originCityName}`
    : `${originCityName} - ${destinationCityName}`;
  const smallScreenTitle = isRTL
    ? `${destinationCityCode} - ${originCityCode}`
    : `${originCityCode} - ${destinationCityCode}`;

  const title = isWiderViewport ? wideScreenTitle : smallScreenTitle;

  return (
    <>
      <ProductModal
        content={t('flexibleTicketPersuasionContent')}
        isOpen={isFlexibleTicketPersuasionModalTouched}
        onConfirm={() => onClickHandler(FLEXIBLE_TICKET)}
        onDecline={() => onClickHandler(STANDARD_TICKET)}
        productName={FlexTicket.name}
        title={t('flexibleTicketHeaderTitle')}
      />
      <Overlay.Drawer
        dataTestId="trip-details-overlay"
        handleClose={onClose}
        isOpen={isOpen}
        shouldCloseOnOverlayClick={!isLoading}
        shouldOpenHorizontally
      >
        <Overlay.HeaderWithBackButton
          className={headerStyles}
          journeyType={t(JOURNEY_TYPE[currentSearchData?.tripType ?? ''])}
          onClose={onClose}
          title={title}
        />
        <Overlay.Content
          className={ticketVersion === 4 || ticketVersion === 5 ? overlayContentStyles : ''}
          data-testid="trip-details-overlay-content"
          innerRef={scollToRef}
        >
          <Card>
            <Stack>
              {shouldShowItineraryInfo && (
                <ItineraryInfo
                  bounds={bounds}
                  isSponsoredTrip={isSponsoredTrip}
                  isVI={isVI}
                  priceDisplayText={priceDisplayText}
                  shareableUrl={shareableUrl}
                  shouldShowSaveTrip={shouldShowSaveTrip}
                  shouldShowShareTrip={shouldShowShareTrip}
                  totalPrice={totalPrice}
                  type={type}
                />
              )}
              {shouldShowOneWayCombinationInfo && (
                <Notification
                  dataTestId="itinerary-drawer-owc-notification"
                  variant={variants.INFORMATION}
                >
                  <Text as="h3" variant={textVariants._16_BOLD}>
                    {t('Air.TripInformation.OneWayCombination')}
                  </Text>
                  <DangerousHtmlContent
                    content={t('Air.TripInformation.OneWayCombination.Tooltip')}
                  />
                </Notification>
              )}
              {shouldShowTripDetails && <TripDetails bounds={bounds} tripType={type} />}
              {!isSeparate && shouldShowBaggageInfo && (
                <BaggageInformation
                  baggageInfo={baggageInfo}
                  dataTestId="baggage-accordion-inside"
                  hasLessThan2hSelfTransfer={hasLessThan2hSelfTransferTrip}
                  isInDrawer
                  isSeparate={isSeparate}
                />
              )}
            </Stack>
          </Card>
          {isSeparate && shouldShowBaggageInfo && (
            <BaggageInformation
              baggageInfo={baggageInfo}
              dataTestId="baggage-accordion-outside"
              hasLessThan2hSelfTransfer={hasLessThan2hSelfTransferTrip}
              isInDrawer
              isSeparate={isSeparate}
            />
          )}
          {isMultipleTickets && shouldShowTickets && (
            <div ref={wrapperRef}>
              <Ticket
                hasError={hasError}
                isLoading={isLoading}
                isRyanAirTicketingOrMarketingCarrierPresent={
                  isRyanAirTicketingOrMarketingCarrierPresent
                }
                isVI={isVI}
                onClick={onClickHandler}
                onSelect={onSelectHandler}
                selectedTicketType={selectedTicketType}
                ticketPriceModalInfo={ticketPriceModalInfo}
                ticketVersion={ticketVersion}
                tickets={tickets}
              />
            </div>
          )}
          {isRyanAirTicketingOrMarketingCarrierPresent && isCFCEnabled && (
            <>
              <CFCInfoCard
                className={titledCardStyles}
                contentText={t('CarrierFlightConfirmation.InfoBox.Description')}
                handleLinkButtonClick={handleCFCTitledCardClick}
                linkButtonText={t('CarrierFlightConfirmation.InfoBox.AnchorButton.Text')}
                title={t('CarrierFlightConfirmation.InfoBox.Title')}
              />
              <CFCModal
                isOpen={isCFCModalOpen}
                setIsOpen={setIsCFCModalOpen}
                shouldShowProceedButton={false}
              />
            </>
          )}
        </Overlay.Content>
        {(!isMultipleTickets || !hideFooterForTicketVersionsList.includes(ticketVersion)) &&
          shouldShowTickets && (
            <Overlay.Footer className={footerClass} innerRef={wrapperRef}>
              <ContinueBooking
                hasError={hasError}
                isLoading={isLoading}
                isMultipleTickets={isMultipleTickets}
                isRyanAirTicketingOrMarketingCarrierPresent={
                  isRyanAirTicketingOrMarketingCarrierPresent
                }
                isVI={isVI}
                onClick={onClickHandler}
                ticket={ticket}
                ticketPriceModalInfo={ticketPriceModalInfo}
                ticketVersion={ticketVersion}
              />
            </Overlay.Footer>
          )}
      </Overlay.Drawer>
    </>
  );
};

export default connect(null, mapDispatchToProps)(ItineraryDrawer);
